/**
 * Public signup page for leads to sign up directly to an agent's client list.
 * URL: /signup/:slug
 * The agent's slug comes from their portal settings.
 */
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API_BASE = (typeof process !== 'undefined' && process.env?.REACT_APP_API_URL) || '/api';

const colors = {
  navy: '#0A1628',
  gold: '#C5A55A',
  gold2: '#B8943F',
  lightBg: '#F0F2F5',
};

const AgentSignupPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [agent, setAgent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!slug) return;
    axios.get(`${API_BASE}/public-leads/signup-info/${slug}`)
      .then(res => {
        if (res.data.success) setAgent(res.data.agent);
        else setNotFound(true);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email.trim()) { setError('Email is required'); return; }
    setSubmitting(true);
    setError('');
    try {
      const res = await axios.post(`${API_BASE}/public-leads/agent-signup`, { slug, ...form });
      if (res.data.success) setSubmitted(true);
      else setError(res.data.message || 'Something went wrong');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to submit. Please try again.');
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: colors.lightBg }}>
        <div style={{ fontSize: '16px', color: '#888' }}>Loading...</div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: colors.lightBg }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ color: colors.navy }}>Agent Not Found</h2>
          <p style={{ color: '#666' }}>This signup link is no longer active or the URL is incorrect.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: colors.lightBg }}>
        <div style={{ textAlign: 'center', maxWidth: '400px', padding: '40px', background: 'white', borderRadius: '16px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>&#10003;</div>
          <h2 style={{ color: colors.navy, marginBottom: '12px' }}>You're Connected!</h2>
          <p style={{ color: '#666', fontSize: '15px' }}>
            {agent?.display_name || 'Your agent'} has been notified and will be in touch shortly.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: colors.lightBg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ width: '100%', maxWidth: '440px', background: 'white', borderRadius: '16px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ background: colors.navy, padding: '28px 24px', textAlign: 'center' }}>
          {agent?.profile_photo_url && (
            <img src={agent.profile_photo_url} alt="" style={{ width: '72px', height: '72px', borderRadius: '50%', border: `3px solid ${colors.gold}`, objectFit: 'cover', marginBottom: '12px' }} />
          )}
          <h2 style={{ color: 'white', fontSize: '20px', fontWeight: 700, margin: 0 }}>
            Connect with {agent?.display_name || 'Your Agent'}
          </h2>
          {agent?.brokerage_name && (
            <div style={{ color: colors.gold, fontSize: '13px', marginTop: '4px', fontWeight: 500 }}>{agent.brokerage_name}</div>
          )}
          {agent?.logo_url && (
            <img src={agent.logo_url} alt="" style={{ maxHeight: '32px', marginTop: '10px', opacity: 0.9 }} />
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
          <p style={{ color: '#666', fontSize: '14px', marginBottom: '20px', textAlign: 'center' }}>
            Enter your information to get started with personalized real estate services.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
            <input
              placeholder="First Name"
              value={form.firstName}
              onChange={e => setForm(p => ({ ...p, firstName: e.target.value }))}
              style={{ padding: '10px 12px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '8px', outline: 'none' }}
            />
            <input
              placeholder="Last Name"
              value={form.lastName}
              onChange={e => setForm(p => ({ ...p, lastName: e.target.value }))}
              style={{ padding: '10px 12px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '8px', outline: 'none' }}
            />
          </div>
          <input
            type="email"
            placeholder="Email Address *"
            required
            value={form.email}
            onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
            style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '8px', outline: 'none', marginBottom: '10px' }}
          />
          <input
            type="tel"
            placeholder="Phone Number"
            value={form.phone}
            onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
            style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '8px', outline: 'none', marginBottom: '10px' }}
          />
          <textarea
            placeholder="Anything you'd like your agent to know? (optional)"
            value={form.message}
            onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
            rows={3}
            style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '8px', outline: 'none', marginBottom: '16px', resize: 'vertical' }}
          />

          {error && <div style={{ color: '#c62828', fontSize: '13px', marginBottom: '12px', textAlign: 'center' }}>{error}</div>}

          <button
            type="submit"
            disabled={submitting}
            style={{
              width: '100%', padding: '12px', fontSize: '15px', fontWeight: 700,
              border: 'none', borderRadius: '8px', cursor: submitting ? 'not-allowed' : 'pointer',
              background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`,
              color: 'white',
            }}
          >
            {submitting ? 'Submitting...' : 'Connect with Agent'}
          </button>
        </form>

        {/* Continue to search option */}
        <div style={{ textAlign: 'center', padding: '0 24px 8px' }}>
          <a
            href={`/${slug}`}
            style={{ fontSize: '13px', color: colors.gold2, textDecoration: 'none', fontWeight: 600 }}
          >
            Skip to Property Search &rarr;
          </a>
        </div>

        <div style={{ textAlign: 'center', padding: '0 24px 16px', fontSize: '11px', color: '#aaa' }}>
          Powered by KairosIQ
        </div>
      </div>
    </div>
  );
};

export default AgentSignupPage;
